import type { LinearInstallations } from '@/generated/prisma/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { brokenIntegrationsQueryKey } from '@/data/integrations/use-broken-integrations';
import { prepareNangoPopup } from '@/data/nango/nango-popup';
import { refetchSelectedSharedPmConnection } from '@/data/nango/shared-pm-realtime';
import { useNangoIntegration } from '@/data/nango/use-nango-integration';
import { onboardingStatusQueryKey } from '@/data/onboarding/use-onboarding-status';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { getIntegrationConnectionState } from '@/lib/integrations/auth-status';
import { REALTIME_KEYS } from '@/lib/realtime/types';
import { captureException } from '@/lib/sentry';
export const linearQueryKey = (installationId?: number) => ['linear-connection', installationId !== undefined ? String(installationId) : 'no-installation'];
const handleConnectAttempt = () => {
  trackClientEvent('linear_connection_attempt', undefined);
};
const handleConnectError = (error: Error, reason?: string) => {
  if (reason === 'popup_blocked') {
    toast.error('Browser blocked the Linear authentication popup. Please allow popups for this site and try again.');
    trackClientEvent('linear_connection_failed', undefined, {
      reason: 'popup_blocked'
    });
    return;
  }
  toast.error(error.message || 'Failed to connect Linear');
  trackClientEvent('linear_connection_failed', undefined, {
    reason: reason || 'auth_error'
  });
};
export function useLinearIntegration(installationId?: number) {
  const queryClient = useQueryClient();
  const queryKey = linearQueryKey(installationId);
  const [isLinkingExistingConnection, setIsLinkingExistingConnection] = useState(false);
  const handleConnectSuccess = (connection: LinearInstallations | null) => {
    queryClient.invalidateQueries({
      queryKey: brokenIntegrationsQueryKey(installationId)
    });
    toast.dismiss('linear-refresh-failure');
    toast.success('Linear account connected');
    trackClientEvent('linear_connection_successful', undefined, {
      organization_id: connection?.organizationId
    });
  };

  // Mutation for updating settings
  const mutation = useMutation({
    mutationFn: async ({
      useContextForReview,
      useContextForPrDescription,
      automaticallyAssociateIssues,
      showLinkedIssueSummary
    }: {
      useContextForReview: boolean;
      useContextForPrDescription: boolean;
      automaticallyAssociateIssues: boolean;
      showLinkedIssueSummary: boolean;
    }) => {
      const params = new URLSearchParams({
        useContextForReview: String(useContextForReview),
        useContextForPrDescription: String(useContextForPrDescription),
        automaticallyAssociateIssues: String(automaticallyAssociateIssues),
        showLinkedIssueSummary: String(showLinkedIssueSummary)
      });

      // Add installationId to the URL if provided
      if (installationId !== undefined) {
        params.append('installationId', String(installationId));
      }
      const res = await fetch(`/api/linear/settings?${params.toString()}`, {
        method: 'PATCH'
      });
      if (!res.ok) {
        throw new Error('Failed to update Linear settings');
      }
      return res.json();
    },
    onMutate: async variables => {
      await queryClient.cancelQueries({
        queryKey
      });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          useContextForReview: variables.useContextForReview,
          useContextForPrDescription: variables.useContextForPrDescription,
          automaticallyAssociateIssues: variables.automaticallyAssociateIssues,
          showLinkedIssueSummary: variables.showLinkedIssueSummary
        };
      });
      return {
        previous
      };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error('Failed to update Linear settings');
    },
    onSuccess: data => {
      queryClient.setQueryData(queryKey, (old_0: any) => {
        if (!old_0) {
          return old_0;
        }
        return {
          ...old_0,
          useContextForReview: data.settings.useContextForReview,
          useContextForPrDescription: data.settings.useContextForPrDescription,
          showLinkedIssueSummary: data.settings.showLinkedIssueSummary,
          ...(typeof data.settings.automaticallyAssociateIssues === 'boolean' && {
            automaticallyAssociateIssues: data.settings.automaticallyAssociateIssues
          })
        };
      });
      toast.success('Linear settings updated');
    }
  });
  const nangoIntegration = useNangoIntegration<LinearInstallations>({
    provider: 'linear',
    getConnectionUrl: installationId ? `/api/linear/connections/get?installationId=${installationId}` : '/api/linear/connections/get',
    disconnectUrl: installationId ? `/api/linear/connections/delete?installationId=${installationId}` : '/api/linear/connections/delete',
    realtimeEventKey: REALTIME_KEYS.LINEAR_CONNECTION_UPDATE,
    queryKey,
    authOptions: {
      authorization_params: {
        actor: 'app'
      }
    },
    installationId,
    enabled: installationId !== undefined,
    getConnectionState: getIntegrationConnectionState,
    callbacks: {
      onConnectAttempt: handleConnectAttempt,
      onConnectSuccess: handleConnectSuccess,
      onConnectError: handleConnectError,
      onDisconnectSuccess: () => {
        toast.success('Linear account disconnected');
      },
      onDisconnectError: () => {
        toast.error('Failed to disconnect Linear');
      },
      onPollingTimeout: attempts => {
        trackClientEvent('linear_connection_failed', undefined, {
          reason: 'polling_timeout',
          attempts
        });
        toast.error('Failed to confirm Linear connection. Please try again.');
      }
    },
    onRealtimeEventAction: (payload, {
      queryClient: queryClient_0,
      queryKey: queryKey_0,
      setIsPolling
    }) => {
      if (payload?.refreshFailure) {
        refetchSelectedSharedPmConnection({
          queryClient: queryClient_0,
          queryKey: queryKey_0,
          setIsPolling
        });
        toast.error('Your Linear connection expired. Please reconnect.', {
          id: 'linear-refresh-failure'
        });
        trackClientEvent('linear_connection_failed', undefined, {
          reason: 'refresh_failure',
          connection_id: payload.refreshFailure.connectionId,
          error_type: payload.refreshFailure.errorType
        });
        return;
      }
      refetchSelectedSharedPmConnection({
        queryClient: queryClient_0,
        queryKey: queryKey_0,
        setIsPolling
      });
    }
  });

  // Helper to get the current connection from the query cache
  function getCurrentConnection() {
    return queryClient.getQueryData(queryKey) as LinearInstallations | undefined;
  }
  const connect = async () => {
    handleConnectAttempt();
    const preparedPopup = prepareNangoPopup();
    if (installationId === undefined) {
      nangoIntegration.connectWithPreparedPopup(preparedPopup);
      return;
    }
    setIsLinkingExistingConnection(true);
    try {
      const response = await fetch(`/api/linear/connections/link-existing?installationId=${installationId}`, {
        method: 'POST'
      });
      const data_0 = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data_0.error || 'Failed to link existing Linear connection');
      }
      const connection_0 = (data_0.connection ?? null) as LinearInstallations | null;
      if (connection_0) {
        queryClient.setQueryData(queryKey, connection_0);
      }
      if (connection_0 && getIntegrationConnectionState(connection_0) === 'connected') {
        preparedPopup.close();
        handleConnectSuccess(connection_0);
        queryClient.invalidateQueries({
          queryKey: onboardingStatusQueryKey(installationId)
        });
        return;
      }
      nangoIntegration.connectWithPreparedPopup(preparedPopup);
    } catch (error) {
      preparedPopup.close();
      const errorInstance = error instanceof Error ? error : new Error(String(error));
      captureException(errorInstance);
      handleConnectError(errorInstance, 'auth_error');
    } finally {
      setIsLinkingExistingConnection(false);
    }
  };
  return {
    ...nangoIntegration,
    connect,
    isConnecting: nangoIntegration.isConnecting || isLinkingExistingConnection,
    updateLinearSettings: (partial: {
      useContextForReview?: boolean;
      useContextForPrDescription?: boolean;
      automaticallyAssociateIssues?: boolean;
      showLinkedIssueSummary?: boolean;
    } = {}) => {
      const current = getCurrentConnection();
      const merged = {
        useContextForReview: typeof partial.useContextForReview === 'boolean' ? partial.useContextForReview : current?.useContextForReview ?? true,
        useContextForPrDescription: typeof partial.useContextForPrDescription === 'boolean' ? partial.useContextForPrDescription : current?.useContextForPrDescription ?? true,
        automaticallyAssociateIssues: typeof partial.automaticallyAssociateIssues === 'boolean' ? partial.automaticallyAssociateIssues : current?.automaticallyAssociateIssues ?? false,
        showLinkedIssueSummary: typeof partial.showLinkedIssueSummary === 'boolean' ? partial.showLinkedIssueSummary : current?.showLinkedIssueSummary ?? true
      };
      mutation.mutate(merged);
    }
  };
}